/**
 * The three preferences the console keeps on this machine: colour scheme, row
 * density, and whether the sidebar is collapsed.
 *
 * ## One store, and local storage is it
 *
 * These were built twice in parallel: once here as component state, and once as
 * a zustand store in `src/store/preferencesStore.ts`, each with its own storage
 * keys. Both wrote `data-scheme` onto the document element, so both appeared to
 * work, and the settings screen carried a comment warning whoever came next
 * that wiring a control to the wrong one of the two would look correct and
 * would not survive a reload. That is the exact failure this file now exists to
 * make impossible, so there is one implementation and the zustand store is
 * gone.
 *
 * What replaced it holds no copy of the values at all: `getSnapshot` reads
 * `localStorage`, the setters write it, and the listener set exists only to
 * tell React to read again. Cached module state was the first version and it
 * has a specific failure that a cache in front of a synchronous, single-writer
 * store is not worth having: the cache is populated once, so the stored value
 * and the value on screen can differ for the rest of the page's life with
 * nothing to reconcile them. Reading through means the two cannot disagree, and
 * `getItem` is a synchronous map lookup that three components call during
 * render, which is not a cost worth designing around.
 *
 * Read through `useSyncExternalStore` rather than `useState`, and that part is
 * load-bearing: the shell reads the scheme to draw the topbar's menu, the
 * settings screen reads it to check a radio, and `App` reads it to write the
 * attribute. Three `useState` calls would be three copies of one fact, and
 * changing it in the topbar would leave the settings screen showing the value
 * it had at mount.
 *
 * They are not application state and not on the server. Nothing is fetched,
 * nothing syncs, and they are wanted before anything has hydrated: at the
 * moment the document first paints. This console is used from shared desks in
 * field offices, so a density somebody chose because of their eyesight is a
 * fact about that desk and must not follow them onto a colleague's screen.
 */

import { useCallback, useLayoutEffect, useSyncExternalStore } from 'react'

/**
 * Tri-state, and byte-identical in meaning to `Appearance` in
 * `namu-app/src/store/settingsStore.ts`: `system` removes the attribute and
 * lets `light-dark()` follow the operating system, the other two pin it.
 *
 * The two products are used by the same organizations and are screenshotted
 * next to each other. A console whose "auto" behaves differently from the
 * phone's "match device" is a support conversation nobody should have to have.
 *
 * The name differs from the app's `Appearance` on purpose. Here the value is
 * also the literal written into `data-scheme` and read by `color-scheme` in
 * `tokens.css`, and naming it after the attribute it becomes is what stops
 * somebody adding a fourth option that the stylesheet has no block for.
 */
export type Scheme = 'system' | 'light' | 'dark'

/**
 * `comfortable` is the absence of a `data-density` attribute, which is exactly
 * how `tokens.css` is written: it defines `compact` and `relaxed` as overrides
 * of the base scale and does not define a third block. Naming the default here
 * rather than calling it "default" keeps the control's three options readable
 * as a scale.
 */
export type Density = 'compact' | 'comfortable' | 'relaxed'

/*
 * One prefix, namespaced away from the mobile app's `namu:` keys.
 *
 * The two products are separate origins in production and share one in local
 * development, where they run on different ports of localhost: a different
 * origin for `fetch`, the same one for `localStorage`, and the same one for a
 * person's expectations. The prefix costs nothing and removes the question.
 */
const SCHEME_KEY = 'namu-console:scheme'
const DENSITY_KEY = 'namu-console:density'
const SIDEBAR_KEY = 'namu-console:sidebar-collapsed'

const SCHEMES: readonly Scheme[] = ['system', 'light', 'dark']
const DENSITIES: readonly Density[] = ['compact', 'comfortable', 'relaxed']

/**
 * Reads and writes are wrapped because `localStorage` throws on *access*, not
 * on use, in a browser with site data blocked, and in Safari's private mode
 * writes throw on quota. An unguarded read here takes the whole console down
 * before it has painted, and it does so only for the people whose browsers are
 * locked down hardest, which is a category that includes the ones on managed
 * organization laptops.
 */
function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // A preference that cannot be remembered is still a preference that works
    // for this session. There is nothing to report and nothing to recover.
  }
}

function readOneOf<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  const stored = read(key)
  return allowed.includes(stored as T) ? (stored as T) : fallback
}

const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Every getter below returns a primitive read straight out of storage, which is
 * what makes reading through legal here.
 *
 * `useSyncExternalStore` compares consecutive snapshots with `Object.is` and
 * loops forever if a getter returns a fresh object each time. A string and a
 * boolean compare by value, so a snapshot taken twice with nothing changed in
 * between is the same snapshot. A getter here that ever needs to return an
 * object has to start caching, and this comment is the reason why.
 *
 * `system` is the fallback where the mobile app deliberately chooses `light`.
 * The app's reason is that Harmattan cream is the brand's primary surface and a
 * phone left in dark mode should not decide a first impression. That argument
 * is about a first impression and it does not survive contact with a tool
 * somebody has open for seven hours: a person who set their machine to dark at
 * nine in the evening has already made this decision, and overriding it every
 * night is the console picking an argument it cannot win.
 */
function getScheme(): Scheme {
  return readOneOf(SCHEME_KEY, SCHEMES, 'system')
}

function getDensity(): Density {
  return readOneOf(DENSITY_KEY, DENSITIES, 'comfortable')
}

function getSidebarCollapsed(): boolean {
  return read(SIDEBAR_KEY) === 'true'
}

/**
 * Writes the two scheme attributes onto the document element, where
 * `tokens.css` reads them.
 *
 * Both are written as the *absence* of an attribute in their default state,
 * because that is how the token file defines the defaults: it declares
 * `[data-scheme="light"]`, `[data-scheme="dark"]`, `[data-density="compact"]`
 * and `[data-density="relaxed"]` and no block for the third value of either.
 * Writing `data-scheme="system"` would match neither pin and would silently
 * mean light for ever.
 */
function applyToDocument(): void {
  const scheme = getScheme()
  if (scheme === 'system') document.documentElement.removeAttribute('data-scheme')
  else document.documentElement.setAttribute('data-scheme', scheme)

  const density = getDensity()
  if (density === 'comfortable') document.documentElement.removeAttribute('data-density')
  else document.documentElement.setAttribute('data-density', density)
}

/**
 * The one path a change takes: storage first, then the document, then React.
 *
 * The document is updated here rather than only in an effect so that the
 * attribute is never a frame behind the value the control is already showing.
 */
function commit(key: string, value: string): void {
  write(key, value)
  applyToDocument()
  for (const listener of listeners) listener()
}

export function useScheme(): [Scheme, (next: Scheme) => void] {
  const scheme = useSyncExternalStore(subscribe, getScheme, getScheme)
  const choose = useCallback((next: Scheme) => commit(SCHEME_KEY, next), [])
  return [scheme, choose]
}

export function useDensity(): [Density, (next: Density) => void] {
  const density = useSyncExternalStore(subscribe, getDensity, getDensity)
  const choose = useCallback((next: Density) => commit(DENSITY_KEY, next), [])
  return [density, choose]
}

export function useSidebarCollapsed(): [boolean, (next: boolean) => void] {
  const collapsed = useSyncExternalStore(subscribe, getSidebarCollapsed, getSidebarCollapsed)
  const choose = useCallback((next: boolean) => commit(SIDEBAR_KEY, String(next)), [])
  return [collapsed, choose]
}

/**
 * Puts the stored preferences onto the document element when a tree mounts.
 *
 * Called by two things, and by both on purpose. `App` calls it because the four
 * public screens have no shell around them, and somebody who pinned the console
 * to dark should not be handed a cream sign-in page on the one screen the
 * product uses to introduce itself. `AppShell` calls it because it is the
 * component that renders the controls for these two values, and a shell whose
 * scheme menu only works when it happens to be mounted inside `App` is a shell
 * that cannot be mounted anywhere else.
 *
 * Calling it twice costs nothing and cannot conflict. It is a projection of one
 * store onto one element, so both callers write the same attribute to the same
 * value; that is precisely the property the two competing preference stores did
 * not have.
 *
 * `useLayoutEffect`, not `useEffect`. `useEffect` runs after the browser has
 * painted, so somebody who pinned dark sees one full frame of Harmattan cream
 * on every reload. It is a flash of exactly the thing they turned off.
 */
export function useConsoleChrome(): void {
  useLayoutEffect(applyToDocument, [])
}

/**
 * The width at which the sidebar stops being a column and becomes a drawer.
 *
 * Staff open this on a tablet in the field, and a 15rem sidebar on a 1024pt
 * landscape screen leaves a table 784pt wide, which is a column and a half of
 * a case list. 900 is the point below which the column is costing more than it
 * gives.
 *
 * It is a number in JavaScript and not in a CSS media query, and that is the
 * whole reason `useMediaQuery` exists here rather than a `@media` block. The
 * shell needs to know which mode it is in: whether to render a menu button,
 * whether the navigation is inside a focus trap, whether Escape should close
 * anything. If CSS also held the breakpoint the number would exist twice, and
 * the window width where the two disagree is a window width where the sidebar
 * is a visible column that a keyboard cannot leave.
 */
export const DRAWER_BREAKPOINT_PX = 900

/**
 * `useSyncExternalStore` rather than `useState` plus an effect.
 *
 * An effect runs after the first paint, so the shell would render one frame as
 * a two-column desk layout on a tablet and then rearrange itself. Reading the
 * query during render means the first frame is already right.
 *
 * `matchMedia` is called without a guard. jsdom does not implement it, and
 * `src/test/domMocks.ts` supplies it once for the whole suite, with the
 * reasoning written out there: a `typeof window.matchMedia === 'function'`
 * check at each call site puts test scaffolding into production code, and it
 * makes the branch that never runs in a browser the only one a test exercises.
 */
export function useMediaQuery(query: string): boolean {
  const subscribeToQuery = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query)
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    },
    [query],
  )

  const snapshot = useCallback(() => window.matchMedia(query).matches, [query])

  return useSyncExternalStore(subscribeToQuery, snapshot)
}
